import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from "react";
import TextInput, { ITextInput, TTextInputProps } from "../TextInput/TextInput";
import CalendarIcon from "../Icons/CalendarIcon";
import IconButton from "../Button/IconButton";
import { useOverlay } from "../Overlay/OverlayProvider";
import Calendar from "./Calendar";
import { TUpdateEventHandler, UpdateEvent } from "../GUIEvents";
import Utils from "../../libs/Utils";
import GUI from "../GUI";

export type TDatePickerProps = Omit<TTextInputProps, "defaultValue"|"value"|"onUpdate"> & {
    defaultValue?:Date
    value?:Date
    onUpdate?:(e:UpdateEvent<Date>) => void
}

function CalendarButton(props:{followId:string,defaultValue:Date|undefined,onUpdate:TUpdateEventHandler<Date>}){
    const {addOverlay,popOverlay} = useOverlay()
    const calendarId = `calendar_${props.followId}`
    const onUpdate = (e:UpdateEvent<Date>) => {
        popOverlay(calendarId)
        props.onUpdate(e)
    }
    const onCalendar = () => {
        addOverlay(calendarId, {children:<Calendar onUpdate={onUpdate} defaultValue={props.defaultValue}/>, anchor:{follow:props.followId}})
    }
    return <IconButton size="small" onClick={onCalendar}><CalendarIcon/></IconButton>
}
export interface IDatePicker {
    value?:Date|undefined
}

var g_id = 0;
export default forwardRef((props:TDatePickerProps, ref?:ForwardedRef<IDatePicker>) => {
    const isManaged = props.hasOwnProperty("value")
    const {defaultValue,value:_value,onUpdate:_onUpdate,onBlur:_onBlur,className,...textProps} = props
    const [value,setValue] = useState<Date|undefined>(defaultValue ?? _value)
    const [text,setText] = useState<string>(value ? Utils.formatDate("yyyy-MM-dd", value) : "")
    const inputRef = useRef<ITextInput|null>(null)
    const current = isManaged ? props.value : value
    const id = useRef<string>(`date_${g_id++}`)
    const onUpdate = (e:UpdateEvent<string>) => {
        const date = e.value ? (Utils.parseDate(e.value)||undefined) : undefined
        setText(e.value||"")
        if (!isManaged) setValue(date)
        _onUpdate?.(new UpdateEvent(e.nativeEvent, date, e.target))
    }
    const onPick = (e:UpdateEvent<Date>) => {
        if (!e.value) setText("")
        else setText(Utils.formatDate("yyyy-MM-dd", e.value))
        if (!isManaged) setValue(e.value||undefined)
        _onUpdate?.(e)
    }
    const onBlur = (e:React.FocusEvent) => {
        const date = Utils.parseDate(inputRef.current?.value)
        const text = date ? Utils.formatDate("yyyy-MM-dd", date) : ""
        if (text && text!=inputRef.current!.value) setText(text)
    }
    useImperativeHandle(ref, () => ({value:current}), [current])
    return <TextInput className={GUI.joinClass("datepicker", className)} endIcon={<CalendarButton followId={id.current} defaultValue={current} onUpdate={onPick}/>} {...textProps} follow-id={id.current} onUpdate={onUpdate} onBlur={onBlur} value={text} ref={inputRef}/>
})